import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { UserCheck, CheckCircle2, ChevronRight, ClipboardList, Activity, Heart, Users, Star } from 'lucide-react';
import ProgrammePageShell from './ProgrammePageShell';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

const BenefitCard = ({ icon: Icon, title, desc }) => (
  <motion.div
    variants={fadeUp}
    className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-lg p-6 transition-all duration-300 hover:-translate-y-1"
  >
    <div className="w-11 h-11 bg-rose-50 rounded-xl flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-[#800000]" />
    </div>
    <h3 className="font-bold text-slate-900 mb-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
  </motion.div>
);

const FAQ = ({ q, a }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left font-semibold text-slate-900 hover:bg-slate-50 transition-colors"
        aria-expanded={open}
      >
        <span>{q}</span>
        <ChevronRight className={`w-5 h-5 text-[#800000] flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-6 pb-4 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-3">{a}</div>}
    </div>
  );
};

const PersonalizedCarePlans = () => {
  const benefits = [
    { icon: ClipboardList, title: 'Comprehensive Health Assessment', desc: 'We start with a full metabolic panel, body composition analysis, and lifestyle evaluation to understand your unique health baseline.' },
    { icon: Activity, title: 'Dynamic & Adaptive Plans', desc: 'Your care plan evolves as you progress. Our team adjusts nutrition, activity, and medical strategies based on real-time results.' },
    { icon: Heart, title: 'Comorbidity Management', desc: 'We address obesity-related conditions like Type 2 diabetes, hypertension, and sleep apnoea as integral parts of your care plan.' },
    { icon: Users, title: 'Multi-disciplinary Team', desc: 'Counsellors, Gastroenterologists, bariatric surgeons and physiotherapists collaborate on a single integrated plan designed just for you.' },
    { icon: Star, title: 'Goal-Oriented Milestones', desc: 'Clear, measurable milestones keep you motivated. We celebrate every victory, from the first 5 kg to metabolic normalization.' },
    { icon: UserCheck, title: 'Ongoing Monitoring', desc: 'Regular follow-up appointments, telehealth check-ins, and a dedicated care coordinator ensure you are never alone on your journey.' },
  ];

  const steps = [
    { step: '01', title: 'Initial Consultation', desc: 'A detailed 60-minute session with our programme director covers your medical history, previous weight loss attempts, goals, and any concerns.' },
    { step: '02', title: 'Diagnostic Workup', desc: 'Blood panels, hormonal profiling, body composition (DEXA or bioimpedance), and metabolic rate testing paint a complete clinical picture.' },
    { step: '03', title: 'Plan Creation', desc: 'Our multi-disciplinary team meets within 48 hours to craft a fully personalised care plan with a nutrition strategy, activity prescription, and medical interventions if needed.' },
    { step: '04', title: 'Implementation & Education', desc: 'A one-to-one session with your dedicated dietitian and physiotherapist gets you started confidently with meal planning, portion guidance, and habit coaching.' },
    { step: '05', title: 'Monthly Review', desc: 'Progress is tracked via clinical markers and body composition. Plans are refined every 4 weeks to ensure you stay on the optimal trajectory.' },
    { step: '06', title: 'Long-term Maintenance', desc: 'After reaching target weight, a 12-month maintenance programme with reduced visit frequency helps you preserve results permanently.' },
  ];

  const faqs = [
    { q: 'How long does a personalised care plan last?', a: 'Plans are designed for a minimum of 6 months with a full 12–18 month support window. The duration depends on your starting BMI, health goals, and the treatment pathway chosen.' },
    { q: 'Is the plan different for patients with diabetes?', a: 'Absolutely. Patients with Type 2 diabetes receive an integrated metabolic plan that co-manages blood glucose alongside weight loss, often reducing or eliminating medication needs under physician supervision.' },
    { q: 'Can I follow the plan while working full time?', a: 'Yes. We design plan around your schedule. Meal plans are practical, telehealth follow-ups save travel time, and our program coordinator provides flexible support via WhatsApp or phone.' },
    { q: 'What if I have tried dieting before and failed?', a: 'Previous failures are valuable data, not a reflection of willpower. We identify exactly what went wrong — whether metabolic, hormonal, behavioural, or unsustainable restriction — and design a plan that addresses those specific barriers.' },
    { q: 'Does insurance cover personalised care plans?', a: 'Coverage varies by insurer. Our administrative team can provide itemised documentation to support your insurance claim. Many patients access cashless treatment through their corporate health benefits.' },
  ];

  return (
    <>
      <Helmet>
        <title>Personalized Care Plans for Obesity | GastroCare Bhopal</title>
        <meta name="description" content="Discover GastroCare Bhopal's personalised obesity care plans — individually tailored nutrition, medical, and lifestyle strategies designed by a multi-disciplinary team for sustainable weight loss." />
        <meta property="og:title" content="Personalized Care Plans | GastroCare Bhopal" />
        <meta property="og:description" content="Individually tailored obesity care plans combining nutrition science, metabolic medicine, and behavioural coaching for lasting results." />
      </Helmet>

      <ProgrammePageShell
        title="Personalized Care Plans"
        subtitle="Every patient is unique. Our personalised care plans integrate medical science, nutrition expertise, and behavioural coaching to create a roadmap designed exclusively for your body, lifestyle, and goals."
        icon={UserCheck}
        breadcrumb="Personalized Care Plans"
      >
        {/* Introduction */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp}>
              <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Why One-Size Doesn't Fit All</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Your Body Is Unique — Your Plan Should Be Too
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Obesity is not a single disease. It is a complex, multi-factorial condition influenced by genetics, hormones, gut microbiome, sleep patterns, stress levels, and socioeconomic factors. A generic "eat less, move more" prescription fails because it ignores this complexity.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                At GastroCare Bhopal, our personalised care plans begin with a thorough diagnostic workup that identifies the specific drivers of weight gain in your case. Whether it is insulin resistance, hypothyroidism, emotional eating, or metabolic adaptation from previous dieting, we treat the root cause — not just the symptom.
              </p>
              <p className="text-slate-600 leading-relaxed">
                The result is a practical, sustainable plan you can follow for life — not a crash diet you abandon within weeks.
              </p>
            </motion.div>
            {/* Image with 98% Patient Satisfaction badge — pb-8 gives room for the overflowing badge */}
            <motion.div variants={fadeUp} className="relative pb-8">
              <img
                src={process.env.PUBLIC_URL + "/serviceimg1.jpg"}
                alt="Doctor consulting patient on personalised care plan"
                className="rounded-2xl w-full h-72 lg:h-96 object-cover shadow-xl"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#800000] text-white rounded-2xl px-5 py-4 shadow-lg hidden md:block z-10">
                <p className="text-2xl font-extrabold leading-none">98%</p>
                <p className="text-sm opacity-90">Patient Satisfaction</p>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Key Benefits */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">What You Gain</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Key Benefits of Our Personalised Plans</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {benefits.map((b, i) => <BenefitCard key={i} {...b} />)}
          </div>
        </motion.section>

        {/* How It Works */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16 bg-slate-50 rounded-3xl p-8 lg:p-12">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>How Your Care Plan Is Built</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <span className="text-4xl font-extrabold text-[#800000]/20 leading-none block mb-3">{s.step}</span>
                <h3 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Who Should Choose This */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="max-w-3xl mx-auto mb-10 text-center">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">Eligibility</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Who Should Choose a Personalised Care Plan?</h2>
            <div className="space-y-4 pt-2">
              {[
                'Adults with a BMI ≥ 27 with at least one weight-related health condition',
                'Patients who have tried and failed multiple conventional diets',
                'Individuals with metabolic conditions: Type 2 diabetes, PCOS, hypothyroidism, fatty liver',
                'Patients preparing for or recovering from bariatric surgery',
                'Anyone seeking a medically supervised, evidence-based weight management programme',
                'Patients who want long-term support rather than a short-term solution',
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#800000] flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.section>

        {/* FAQs */}
        <motion.section variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-16">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <span className="text-[#800000] font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Frequently Asked Questions</h2>
          </motion.div>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => <FAQ key={i} q={f.q} a={f.a} />)}
          </div>
        </motion.section>
      </ProgrammePageShell>
    </>
  );
};

export default PersonalizedCarePlans;
